import React, { useState, useEffect, memo, useReducer, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ContactMail as ContactMailIcon,
  Link as LinkIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  CalendarMonth as CalendarMonthIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  Tag as TagIcon,
  PhotoCamera as PhotoCameraIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { candidatesAPI } from '../services/api';

// Configure PDF.js worker to use local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Stable TabPanel component defined outside of EditCandidate to avoid remounts on each render
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

// Form data reducer for more efficient state management
const formDataReducer = (state, action) => {
  switch (action.type) {
    case 'INIT':
      return { ...action.payload };
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.field]: action.value
      };
    case 'UPDATE_PARSED_FIELD':
      return {
        ...state,
        parsed: {
          ...state.parsed,
          [action.field]: action.value
        }
      };
    case 'UPDATE_ARRAY_FIELD':
      return {
        ...state,
        parsed: {
          ...state.parsed,
          [action.field]: state.parsed[action.field].map((item, i) =>
            i === action.index ? action.value : item
          )
        }
      };
    default:
      return state;
  }
};

const EditCandidate = ({ open, onClose, candidate, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentCandidateId, setCurrentCandidateId] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const profilePhotoRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoEditOptions, setShowPhotoEditOptions] = useState(false);
  const [isPhotoHover, setIsPhotoHover] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  // CV-related state
  const [cvBlob, setCvBlob] = useState(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const cvBlobRef = useRef(null);
  const [formData, dispatchFormData] = useReducer(formDataReducer, {
    // Basic Info
    parsed: {
      full_name: '',
      emails: [''],
      phones: [''],
      location: '',
      summary: '',
      skills: [],
      education: [],
      experience: [],
      links: [],
      linkedIn: '',
      personalWebsite: '',
      rightToWorkInAustralia: false
    },
    // HR Fields
    startDate: '',
    salaryExpectation: '',
    communication: 'average',
    availableForFreelance: false,
    freelanceDayRate: '',
    notes: '',
    status: 'unreviewed',
    interviewed: 'no'
  });

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Load profile photo
  const loadProfilePhoto = async (candidateId, force = false) => {
    try {
      const response = await candidatesAPI.getProfilePhoto(candidateId, { cacheBust: force ? Date.now() : undefined });
      const url = response.data?.url || null;
      profilePhotoRef.current = url;
      setProfilePhoto(url);
    } catch (error) {
      // No profile photo found or error loading - this is normal
      profilePhotoRef.current = null;
      setProfilePhoto(null);
    }
  };

  // Load CV PDF
  const loadCV = useCallback(async (candidateId, force = false) => {
    if (!force && cvBlobRef.current) {
      return; // Already loaded
    }

    setCvLoading(true);
    setCvError(null);

    try {
      // Import cvFilesAPI here to avoid circular imports
      const { cvFilesAPI } = await import('../services/api');

      if (!candidate?.cvFileId?._id) {
        throw new Error('No CV file available for this candidate');
      }

      const response = await cvFilesAPI.download(candidate.cvFileId._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Clean up previous blob URL
      if (cvBlobRef.current) {
        try { URL.revokeObjectURL(cvBlobRef.current); } catch {}
      }

      const blobUrl = URL.createObjectURL(blob);
      cvBlobRef.current = blobUrl;
      setCvBlob(blobUrl);
      setNumPages(null); // Reset page count
    } catch (error) {
      console.error('Failed to load CV:', error);
      setCvError('Failed to load CV. Please try again.');
    } finally {
      setCvLoading(false);
    }
  }, [candidate]);

  const handleDownloadCV = useCallback(async () => {
    try {
      if (!candidate?.cvFileId?._id) {
        console.error('No CV file ID available for download');
        return;
      }
      const { cvFilesAPI } = await import('../services/api');
      const response = await cvFilesAPI.download(candidate.cvFileId._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = candidate.cvFileId.filename || `${candidate.parsed?.full_name || 'candidate'}_cv.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CV:', error);
    }
  }, [candidate]);

  // Initialize form data when candidate changes
  useEffect(() => {
    if (candidate && candidate._id && candidate._id !== currentCandidateId) {
      const newFormData = {
        parsed: {
          full_name: candidate.parsed?.full_name || '',
          emails: Array.isArray(candidate.parsed?.emails) && candidate.parsed.emails.length > 0
            ? candidate.parsed.emails
            : [''],
          phones: Array.isArray(candidate.parsed?.phones) && candidate.parsed.phones.length > 0
            ? candidate.parsed.phones
            : [''],
          location: candidate.parsed?.location || '',
          summary: candidate.parsed?.summary || '',
          skills: Array.isArray(candidate.parsed?.skills) ? candidate.parsed.skills : [],
          education: Array.isArray(candidate.parsed?.education) ? candidate.parsed.education : [],
          experience: Array.isArray(candidate.parsed?.experience) ? candidate.parsed.experience : [],
          links: Array.isArray(candidate.parsed?.links) ? candidate.parsed.links : [],
          linkedIn: candidate.parsed?.linkedIn || '',
          personalWebsite: candidate.parsed?.personalWebsite || '',
          rightToWorkInAustralia: Boolean(candidate.parsed?.rightToWorkInAustralia)
        },
        startDate: candidate.startDate ? candidate.startDate.split('T')[0] : '',
        salaryExpectation: candidate.salaryExpectation ? String(candidate.salaryExpectation) : '',
        communication: candidate.communication || 'average',
        availableForFreelance: Boolean(candidate.availableForFreelance),
        freelanceDayRate: candidate.freelanceDayRate ? String(candidate.freelanceDayRate) : '',
        notes: candidate.notes || '',
        status: candidate.status || 'unreviewed',
        interviewed: candidate.interviewed || 'no'
      };

      dispatchFormData({ type: 'INIT', payload: newFormData });
      setCurrentCandidateId(candidate._id);
      setError(null);

      // Load profile photo
      loadProfilePhoto(candidate._id);

      // Load CV for CV tab
      loadCV(candidate._id);
    }
  }, [candidate, currentCandidateId, loadCV]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentCandidateId(null);
      setActiveTab(0);
      setError(null);
      if (profilePhotoRef.current) {
        try { URL.revokeObjectURL(profilePhotoRef.current); } catch {}
        profilePhotoRef.current = null;
      }
      setProfilePhoto(null);

      // Clean up CV blob URL
      if (cvBlobRef.current) {
        try { URL.revokeObjectURL(cvBlobRef.current); } catch {}
        cvBlobRef.current = null;
      }
      setCvBlob(null);
      setCvError(null);
      setNumPages(null);
    }
  }, [open]);

  // Cleanup on unmount (must not be conditional)
  useEffect(() => {
    return () => {
      if (profilePhotoRef.current) {
        try { URL.revokeObjectURL(profilePhotoRef.current); } catch {}
        profilePhotoRef.current = null;
      }
      if (cvBlobRef.current) {
        try { URL.revokeObjectURL(cvBlobRef.current); } catch {}
        cvBlobRef.current = null;
      }
    };
  }, []);

  const handleFieldChange = (field, value) => {
    dispatchFormData({ type: 'UPDATE_FIELD', field, value });
  };

  const handleParsedFieldChange = (field, value) => {
    dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field, value });
  };

  const handleArrayFieldChange = (field, index, value) => {
    dispatchFormData({ type: 'UPDATE_ARRAY_FIELD', field, index, value });
  };

  // Enhanced change handlers for better controlled component behavior
  const handleInputChange = (field, event) => {
    const value = event.target.value;
    handleFieldChange(field, value);
  };

  const handleParsedInputChange = (field, event) => {
    const value = event.target.value;
    handleParsedFieldChange(field, value);
  };

  const handleArrayInputChange = (field, index, event) => {
    const value = event.target.value;
    handleArrayFieldChange(field, index, value);
  };

  const handleNumberInputChange = (field, event) => {
    const value = event.target.value;
    // Convert to number or keep as string for controlled component
    const numValue = value === '' ? '' : Number(value);
    handleFieldChange(field, numValue);
  };

  const addArrayItem = (field, defaultValue = '') => {
    const newArray = [...formData.parsed[field], defaultValue];
    dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field, value: newArray });
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData.parsed[field].filter((_, i) => i !== index);
    dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field, value: newArray });
  };

  // Profile photo handlers
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    setError(null);

    try {
      await candidatesAPI.uploadProfilePhoto(candidate._id, file);
      // Reload the profile photo
      await loadProfilePhoto(candidate._id, true);
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      setError('Failed to upload profile photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    setUploadingPhoto(true);
    setError(null);

    try {
      await candidatesAPI.deleteProfilePhoto(candidate._id);
      if (profilePhotoRef.current) {
        try { URL.revokeObjectURL(profilePhotoRef.current); } catch {}
        profilePhotoRef.current = null;
      }
      setProfilePhoto(null);
    } catch (error) {
      console.error('Failed to delete profile photo:', error);
      setError('Failed to delete profile photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePastePhoto = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        
        // Create a synthetic event for the upload handler
        const syntheticEvent = {
          target: {
            files: [file]
          }
        };
        
        await handlePhotoUpload(syntheticEvent);
        break;
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Clean up empty array items
      const cleanedData = {
        ...formData,
        parsed: {
          ...formData.parsed,
          emails: formData.parsed.emails.filter(email => email.trim()),
          phones: formData.parsed.phones.filter(phone => phone.trim()),
          links: formData.parsed.links.filter(link => link.trim()),
          skills: formData.parsed.skills.filter(skill => skill.trim()),
          education: formData.parsed.education.filter(edu => edu.institution?.trim()),
          experience: formData.parsed.experience.filter(exp => exp.company?.trim())
        }
      };

      await candidatesAPI.update(candidate._id, cleanedData);
      onSave && onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Failed to save candidate:', error);
      setError('Failed to save candidate. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      await candidatesAPI.delete(candidate._id);
      // Call onSave with null to indicate deletion to parent component
      onSave && onSave(null);
      onClose();
    } catch (error) {
      console.error('Failed to delete candidate:', error);
      setError('Failed to delete candidate. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  if (!candidate) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {getInitials(formData.parsed.full_name || candidate.parsed?.full_name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {formData.parsed.full_name || candidate.parsed?.full_name || 'Unknown Candidate'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {(formData.parsed.emails?.[0] || candidate.parsed?.emails?.[0]) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formData.parsed.emails?.[0] || candidate.parsed?.emails?.[0]}
                  </Typography>
                </Box>
              )}
              {(formData.parsed.location || candidate.parsed?.location) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formData.parsed.location || candidate.parsed?.location}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={`Status: ${formData.status}`} color="default" variant="outlined" />
          <Chip size="small" label={`Interview: ${formData.interviewed}`} variant="outlined" />
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {error && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minWidth: 120 }
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Basic Info" />
          <Tab icon={<ContactMailIcon />} iconPosition="start" label="Contact" />
          <Tab icon={<LinkIcon />} iconPosition="start" label="Skills & Links" />
          <Tab icon={<SchoolIcon />} iconPosition="start" label="Education" />
          <Tab icon={<WorkIcon />} iconPosition="start" label="Experience" />
          <Tab icon={<AssignmentIcon />} iconPosition="start" label="HR Notes" />
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="CV" />
        </Tabs>

        {/* Basic Info Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box onPaste={handlePastePhoto}>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.parsed.full_name}
                onChange={(e) => handleParsedInputChange('full_name', e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                placeholder="e.g. Alex Johnson"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.parsed.location}
                onChange={(e) => handleParsedInputChange('location', e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                placeholder="City, Country"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  label="Status"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="unreviewed">Unreviewed</MenuItem>
                  <MenuItem value="not_suitable">Not Suitable</MenuItem>
                  <MenuItem value="maybe">Maybe</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="offer_made">Offer Made</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                  <MenuItem value="duplicate">Duplicate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Interview Status</InputLabel>
                <Select
                  value={formData.interviewed}
                  onChange={(e) => handleFieldChange('interviewed', e.target.value)}
                  label="Interview Status"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Profile Photo
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}
                  onMouseEnter={() => setIsPhotoHover(true)}
                  onMouseLeave={() => setIsPhotoHover(false)}
                >
                  <Box
                    sx={{
                      position: 'relative'
                    }}
                  >
                    <Avatar
                      src={profilePhoto}
                      sx={{
                        width: 120,
                        height: 120,
                        border: '2px solid',
                        borderColor: 'divider',
                        cursor: profilePhoto ? 'pointer' : 'default'
                      }}
                      onClick={() => { if (profilePhoto) setPhotoPreviewOpen(true); }}
                    >
                      {getInitials(formData.parsed.full_name || candidate.parsed?.full_name)}
                    </Avatar>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setShowPhotoEditOptions(!showPhotoEditOptions); }}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        border: '2px solid',
                        borderColor: 'divider',
                        opacity: isPhotoHover ? 1 : 0,
                        visibility: isPhotoHover ? 'visible' : 'hidden',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      minWidth: 260,
                      opacity: showPhotoEditOptions ? 1 : 0,
                      visibility: showPhotoEditOptions ? 'visible' : 'hidden',
                      pointerEvents: showPhotoEditOptions ? 'auto' : 'none',
                      transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out'
                    }}
                  >
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="profile-photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                    />
                    <label htmlFor="profile-photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                    {profilePhoto && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handlePhotoDelete}
                        disabled={uploadingPhoto}
                        size="small"
                      >
                        Remove Photo
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Paste from clipboard (Ctrl+V) or click to upload
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={12} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Professional Summary"
                value={formData.parsed.summary}
                onChange={(e) => handleParsedInputChange('summary', e)}
                placeholder="Brief summary of the candidate's professional background..."
                helperText="1-3 sentences works best"
              />
            </Grid>
          </Grid>
          </Box>
        </TabPanel>

        {/* Contact Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            {formData.parsed.emails.map((email, index) => (
              <Grid item xs={12} key={`email-${index}`}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label={`Email ${index + 1}`}
                    type="email"
                    value={email}
                    onChange={(e) => handleArrayInputChange('emails', index, e)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                  {formData.parsed.emails.length > 1 && (
                    <Tooltip title="Remove email">
                      <IconButton
                        color="error"
                        onClick={() => removeArrayItem('emails', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => addArrayItem('emails', '')}
                variant="outlined"
                size="small"
              >
                Add Email
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {formData.parsed.phones.map((phone, index) => (
              <Grid item xs={12} key={`phone-${index}`}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label={`Phone ${index + 1}`}
                    value={phone}
                    onChange={(e) => handleArrayInputChange('phones', index, e)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                  {formData.parsed.phones.length > 1 && (
                    <Tooltip title="Remove phone">
                      <IconButton
                        color="error"
                        onClick={() => removeArrayItem('phones', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => addArrayItem('phones', '')}
                variant="outlined"
                size="small"
              >
                Add Phone
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Skills & Links Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Skills
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {formData.parsed.skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill || `Skill ${index + 1}`}
                    onDelete={() => removeArrayItem('skills', index)}
                    variant="outlined"
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                label="Add Skill"
                placeholder="Enter a skill and press Enter"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    handleParsedFieldChange('skills', [...formData.parsed.skills, e.target.value.trim()]);
                    e.target.value = '';
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TagIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                helperText="Press Enter to add as a chip"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Links & Social Media
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 350 }}
                  label="LinkedIn Profile"
                  value={formData.parsed.linkedIn}
                  onChange={(e) => handleParsedInputChange('linkedIn', e)}
                  placeholder="https://linkedin.com/in/username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  fullWidth
                  sx={{ minWidth: 350 }}
                  label="Personal Website"
                  value={formData.parsed.personalWebsite}
                  onChange={(e) => handleParsedInputChange('personalWebsite', e)}
                  placeholder="https://yourwebsite.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />

                {formData.parsed.links.map((link, index) => (
                  <Box key={`link-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      sx={{ minWidth: 350 }}
                      label={`Link ${index + 1}`}
                      value={link}
                      onChange={(e) => handleArrayInputChange('links', index, e)}
                      placeholder="https://example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                    <Tooltip title="Remove link">
                      <IconButton
                        color="error"
                        onClick={() => removeArrayItem('links', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addArrayItem('links', '')}
                  variant="outlined"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Link
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Education Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Education
          </Typography>
          {formData.parsed.education.map((edu, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Education {index + 1}
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const newEducation = formData.parsed.education.filter((_, i) => i !== index);
                      dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Institution"
                      value={edu.institution || ''}
                      onChange={(e) => {
                        const newEducation = [...formData.parsed.education];
                        newEducation[index] = { ...newEducation[index], institution: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                      }}
                      placeholder="University / Institute"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Degree"
                      value={edu.degree || ''}
                      onChange={(e) => {
                        const newEducation = [...formData.parsed.education];
                        newEducation[index] = { ...newEducation[index], degree: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                      }}
                      placeholder="B.Sc, M.Sc, Diploma, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Field of Study"
                      value={edu.field || ''}
                      onChange={(e) => {
                        const newEducation = [...formData.parsed.education];
                        newEducation[index] = { ...newEducation[index], field: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                      }}
                      placeholder="Computer Science, Marketing, ..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Start Year"
                      type="number"
                      value={edu.start_year || ''}
                      onChange={(e) => {
                        const newEducation = [...formData.parsed.education];
                        newEducation[index] = { ...newEducation[index], start_year: e.target.value ? parseInt(e.target.value) : null };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="End Year"
                      type="number"
                      value={edu.end_year || ''}
                      onChange={(e) => {
                        const newEducation = [...formData.parsed.education];
                        newEducation[index] = { ...newEducation[index], end_year: e.target.value ? parseInt(e.target.value) : null };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              const newEducation = [...formData.parsed.education, {
                institution: '',
                degree: '',
                field: '',
                start_year: null,
                end_year: null
              }];
              dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'education', value: newEducation });
            }}
            variant="outlined"
          >
            Add Education
          </Button>
        </TabPanel>

        {/* Experience Tab */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Work Experience
          </Typography>
          {formData.parsed.experience.map((exp, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Experience {index + 1}
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const newExperience = formData.parsed.experience.filter((_, i) => i !== index);
                      dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={exp.company || ''}
                      onChange={(e) => {
                        const newExperience = [...formData.parsed.experience];
                        newExperience[index] = { ...newExperience[index], company: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                      }}
                      placeholder="Company name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={exp.title || ''}
                      onChange={(e) => {
                        const newExperience = [...formData.parsed.experience];
                        newExperience[index] = { ...newExperience[index], title: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                      }}
                      placeholder="Role title"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="month"
                      value={exp.start_date || ''}
                      onChange={(e) => {
                        const newExperience = [...formData.parsed.experience];
                        newExperience[index] = { ...newExperience[index], start_date: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="month"
                      value={exp.end_date || ''}
                      onChange={(e) => {
                        const newExperience = [...formData.parsed.experience];
                        newExperience[index] = { ...newExperience[index], end_date: e.target.value };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Key Responsibilities/Achievements
                    </Typography>
                    {(exp.bullets || []).map((bullet, bulletIndex) => (
                      <Box key={bulletIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={bullet}
                          onChange={(e) => {
                            const newExperience = [...formData.parsed.experience];
                            const newBullets = [...(newExperience[index].bullets || [])];
                            newBullets[bulletIndex] = e.target.value;
                            newExperience[index] = { ...newExperience[index], bullets: newBullets };
                            dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                          }}
                          placeholder="Describe key responsibility or achievement..."
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newExperience = [...formData.parsed.experience];
                            const newBullets = (newExperience[index].bullets || []).filter((_, i) => i !== bulletIndex);
                            newExperience[index] = { ...newExperience[index], bullets: newBullets };
                            dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newExperience = [...formData.parsed.experience];
                        const newBullets = [...(newExperience[index].bullets || []), ''];
                        newExperience[index] = { ...newExperience[index], bullets: newBullets };
                        dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
                      }}
                      variant="outlined"
                      sx={{ mt: 1 }}
                    >
                      Add Responsibility
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              const newExperience = [...formData.parsed.experience, {
                company: '',
                title: '',
                start_date: '',
                end_date: '',
                bullets: ['']
              }];
              dispatchFormData({ type: 'UPDATE_PARSED_FIELD', field: 'experience', value: newExperience });
            }}
            variant="outlined"
          >
            Add Experience
          </Button>
        </TabPanel>

        {/* HR Notes Tab */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleFieldChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Salary Expectation"
                type="number"
                value={formData.salaryExpectation || ''}
                onChange={(e) => handleNumberInputChange('salaryExpectation', e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  'data-testid': 'salary-input'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Communication Skills</InputLabel>
                <Select
                  value={formData.communication}
                  onChange={(e) => handleFieldChange('communication', e.target.value)}
                  label="Communication Skills"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="poor">Poor</MenuItem>
                  <MenuItem value="average">Average</MenuItem>
                  <MenuItem value="good">Good</MenuItem>
                  <MenuItem value="excellent">Excellent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.availableForFreelance}
                    onChange={(e) => handleFieldChange('availableForFreelance', e.target.checked)}
                  />
                }
                label="Available for Freelance"
              />
            </Grid>
            {formData.availableForFreelance && (
              <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Freelance Day Rate"
                type="number"
                value={formData.freelanceDayRate || ''}
                onChange={(e) => handleNumberInputChange('freelanceDayRate', e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  'data-testid': 'freelance-rate-input'
                }}
              />
              </Grid>
            )}
            <Grid item xs={12} />
            <Grid item xs={12} sm={12} md={12} sx={{ mt: 2, flexBasis: '100%', maxWidth: '100%', width: '100%' }}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                label="Internal Notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e)}
                placeholder="Internal HR notes about this candidate..."
                sx={{
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      minHeight: '200px',
                      resize: 'vertical'
                    }
                  }
                }}
                inputProps={{
                  'data-testid': 'notes-input'
                }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* CV Tab */}
        <TabPanel value={activeTab} index={6}>
          <Box sx={{ width: '100%', height: '70vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                CV Document
              </Typography>
              <Tooltip title="Download CV">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleDownloadCV}
                    disabled={!candidate?.cvFileId?._id}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {!candidate?.cvFileId?._id ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                textAlign: 'center'
              }}>
                <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No CV file available for this candidate.
                </Typography>
              </Box>
            ) : cvLoading ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh'
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading CV...
                </Typography>
              </Box>
            ) : cvError ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                textAlign: 'center'
              }}>
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                  {cvError}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => loadCV(candidate._id, true)}
                  startIcon={<DescriptionIcon />}
                >
                  Retry Loading CV
                </Button>
              </Box>
            ) : cvBlob ? (
              <Box sx={{
                height: '60vh',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '& .react-pdf__Document': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                },
                '& .react-pdf__Page': {
                  maxWidth: 'calc(100% - 20px)',
                  marginBottom: 2,
                  boxShadow: 1,
                }
              }}>
                <Document
                  file={cvBlob}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={(error) => {
                    console.error('Error loading PDF document:', error);
                    setCvError(`Failed to load PDF document: ${error.message || 'Unknown error'}`);
                  }}
                  loading={
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '60vh'
                    }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading PDF document...
                      </Typography>
                    </Box>
                  }
                  error={
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '60vh',
                      textAlign: 'center'
                    }}>
                      <DescriptionIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                      <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                        Failed to load PDF document
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This might be due to network issues or an unsupported PDF format.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => loadCV(candidate._id, true)}
                        startIcon={<DescriptionIcon />}
                      >
                        Retry Loading CV
                      </Button>
                    </Box>
                  }
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={1.2}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading=""
                      error=""
                    />
                  ))}
                </Document>
              </Box>
            ) : null}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={() => setDeleteConfirmOpen(true)}
          color="error"
          startIcon={<DeleteIcon />}
          disabled={saving || deleting}
        >
          Delete Candidate
        </Button>
        <Box>
          <Button
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            disabled={saving || deleting}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={saving || deleting}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogActions>

      {/* Photo Preview Dialog */}
      <Dialog
        open={photoPreviewOpen}
        onClose={() => setPhotoPreviewOpen(false)}
        maxWidth={false}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pb: 1 }}>
          <IconButton onClick={() => setPhotoPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '50vw', minHeight: '50vh', p: 2 }}>
            {profilePhoto && (
              <img
                src={profilePhoto}
                alt="Profile"
                style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this candidate?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. This will permanently delete the candidate,
            their CV file, and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default memo(EditCandidate, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.open === nextProps.open &&
    prevProps.candidate?._id === nextProps.candidate?._id &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.onSave === nextProps.onSave
  );
});
